import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PRODUCTS, CATEGORIES as CATEGORY_META } from '../../constants';

const CATEGORIES = [
  { name: 'Machetes', sub: 'Blades & tools', categoryKey: 'Machetes' },
  { name: 'Flag Bandanas', sub: 'Country flag bandanas', categoryKey: 'Flag Bandanas' },
  { name: 'Caps & Hats', sub: 'Caps & fishing hats', categoryKey: 'Caps & Fishing Hats' },
  { name: 'Keychains', sub: 'Flag keychains', categoryKey: 'Keychains & Collectables' },
  { name: 'Sunglasses', sub: 'Flag print shades', categoryKey: 'Flag Sunglasses' },
  { name: 'Hoop Earrings', sub: 'Flag hoop earrings', categoryKey: "Women's Wear" },
  { name: 'Smudge Sticks', sub: 'Sage & palo santo', categoryKey: 'Smudge Sticks' },
  { name: 'Full Size Flags', sub: '3x5 ft country flags', categoryKey: 'Full Size Flags' },
  { name: 'Mirror Trinkets', sub: 'Car mirror hangers', categoryKey: 'Car Mirror Trinkets' },
  { name: 'Windbreakers', sub: 'Flag print jackets', categoryKey: 'Windbreakers' },
];

// Get the first product image for each category
const getCategoryImage = (categoryKey: string): string | null => {
  const product = PRODUCTS.find(p => p.category === categoryKey);
  return product?.image || null;
};

// Get the emoji fallback for a category
const getCategoryEmoji = (categoryKey: string): string => {
  const meta = CATEGORY_META.find(c => c.name === categoryKey);
  return meta?.emoji || '📦';
};

const CategoryCard: React.FC<{ cat: typeof CATEGORIES[0] }> = ({ cat }) => {
  const [imgError, setImgError] = useState(false);
  const image = getCategoryImage(cat.categoryKey);
  const emoji = getCategoryEmoji(cat.categoryKey);

  return (
    <Link
      to="/shop"
      aria-label={`Browse ${cat.name}`}
      className="bg-white rounded-2xl p-5 sm:p-6 border border-gray-100 text-center cursor-pointer transition-all hover:border-brand-red hover:shadow-lg hover:shadow-brand-red/10 hover:-translate-y-1 relative overflow-hidden group"
    >
      <div className="absolute bottom-0 left-0 w-full h-[3px] bg-gradient-to-r from-brand-red to-brand-red-light opacity-0 group-hover:opacity-100 transition-opacity" />

      {/* Product photo thumbnail */}
      <div className="w-[64px] h-[64px] rounded-full mx-auto mb-3 overflow-hidden bg-brand-light-bg flex items-center justify-center">
        {image && !imgError ? (
          <img
            src={image}
            alt={cat.name}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="text-2xl">{emoji}</span>
        )}
      </div>

      <div className="font-display font-bold text-sm text-brand-dark mb-0.5">{cat.name}</div>
      <div className="text-[12px] text-brand-text-muted">{cat.sub}</div>
    </Link>
  );
};

const ProductCategories: React.FC = () => {
  return (
    <section
      className="relative py-16 sm:py-20 overflow-hidden"
      style={{ backgroundImage: "url('/images/categories/categories-bg.webp')", backgroundSize: 'cover', backgroundPosition: 'center' }}
    >
      <div className="absolute inset-0 bg-white/75 z-[1]" aria-hidden="true" />

      <div className="relative z-[2] max-w-[1280px] mx-auto px-5 sm:px-10">
        <div className="text-center mb-12">
          <div className="text-xs font-bold uppercase tracking-[2px] text-brand-red mb-3">Product Categories</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-brand-dark mb-3">Everything Your Store Needs</h2>
          <p className="text-base text-brand-text-muted max-w-[520px] mx-auto leading-relaxed">
            Authentic multicultural products at wholesale prices — sold by the box, with mix & match on select categories.
          </p>
        </div>

        {/* Balanced 5+5 grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {CATEGORIES.slice(0, 5).map((cat) => (
            <CategoryCard key={cat.name} cat={cat} />
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mt-4">
          {CATEGORIES.slice(5).map((cat) => (
            <CategoryCard key={cat.name} cat={cat} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductCategories;
